import { useState, useMemo, Fragment, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api, fmt, fmtMoney, formatErr, invalidateSaleQueries } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal, { inputCls, labelCls, btnPrimary, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { TableCard, StatusBadge, EmptyRow, SkeletonRows, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import Pagination from "@/components/Pagination";
import { Download, ChevronDown, Receipt, Undo2, Pencil, Search, Trash2, ShoppingBag, Wallet, HandCoins, CalendarDays } from "lucide-react";
import { downloadInvoice } from "@/lib/pdf";
import { toast } from "sonner";

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const PAGE = 20;
const todayInput = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" });
const monthStart = () => todayInput().slice(0, 7) + "-01";
const TABS = [{ key: "invoices", label: "Invoices" }, { key: "returns", label: "Returns" }];

function Stat({ label, value, icon: Icon, tone = "forest", hint }) {
  const toneCls = tone === "danger" ? "text-agri-danger bg-red-50" : tone === "amber" ? "text-agri-amber-dark bg-agri-amber/10" : "text-agri-forest bg-agri-forest/10";
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-cap">{label}</div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-agri-ink truncate">{value}</div>
          {hint && <div className="text-[11px] text-agri-ink-2 mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 shrink-0 rounded-lg grid place-items-center ${toneCls}`}><Icon size={18} /></div>
      </div>
    </div>
  );
}

export default function SalesHistory() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canReturn = user?.role === "admin" || user?.role === "manager";
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [tab, setTab] = useState("invoices");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [method, setMethod] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [page, setPage] = useState(0);
  const [retSale, setRetSale] = useState(null);
  const [retItems, setRetItems] = useState({});
  const [retSettle, setRetSettle] = useState("credit");
  useEffect(() => { setPage(0); }, [start, end, method, tab]);

  const { data, isLoading } = useQuery({
    queryKey: ["sales", tab, start, end, method, page],
    queryFn: () => api.get("/sales", {
      params: { kind: tab === "returns" ? "returns" : "sales", start: start || undefined, end: end || undefined, payment_method: method, skip: page * PAGE, limit: PAGE }
    }).then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] ?? r.data.length) })),
    placeholderData: keepPreviousData,
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  // Summary (manager-only figures)
  const { data: repAll } = useQuery({ queryKey: ["report-sales", "all"], queryFn: () => api.get("/reports/sales").then((r) => r.data), enabled: canEdit });
  const { data: repMonth } = useQuery({ queryKey: ["report-sales", "month", monthStart()], queryFn: () => api.get("/reports/sales", { params: { start: monthStart(), end: todayInput() } }).then((r) => r.data), enabled: canEdit });
  const { data: fp } = useQuery({ queryKey: ["financial-position"], queryFn: () => api.get("/reports/financial-position").then((r) => r.data), enabled: canEdit });

  // Products/clients only for the editor.
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data), enabled: canEdit });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data), enabled: canEdit });

  // ---- Edit bill state ----
  const [editSale, setEditSale] = useState(null);
  const [ecart, setEcart] = useState([]);
  const [ediscount, setEdiscount] = useState(0);
  const [emethod, setEmethod] = useState("cash");
  const [eclientId, setEclientId] = useState("");
  const [epaid, setEpaid] = useState(0);
  const [edate, setEdate] = useState("");
  const [eOrigDate, setEOrigDate] = useState("");
  const [eOrigQty, setEOrigQty] = useState({});
  const [esearch, setEsearch] = useState("");

  const openEdit = (s) => {
    const origQty = Object.fromEntries(s.items.map((it) => [it.product_id, it.quantity]));
    setEcart(s.items.map((it) => {
      const p = products.find((pp) => pp.id === it.product_id);
      return {
        product: p || { id: it.product_id, name: it.product_name, selling_price: it.unit_price, stock_qty: Infinity, unit: "" },
        qty: it.quantity,
        discount: it.discount || 0,
      };
    }));
    setEOrigQty(origQty);
    setEdiscount(s.discount || 0);
    setEmethod(s.payment_method);
    setEclientId(s.client_id || "");
    setEpaid(s.paid_amount || 0);
    const d = new Date(s.created_at).toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" });
    setEdate(d); setEOrigDate(d);
    setEsearch("");
    setEditSale(s);
  };

  const maxQty = (p) => (p.stock_qty === Infinity ? Infinity : (p.stock_qty || 0) + (eOrigQty[p.id] || 0));
  const eSubtotal = ecart.reduce((sum, c) => sum + Math.max(0, num(c.qty) * c.product.selling_price - num(c.discount)), 0);
  const eTotal = Math.max(0, eSubtotal - (parseFloat(ediscount) || 0));

  const eFiltered = useMemo(() => {
    if (!esearch) return [];
    const s = esearch.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(s) || (p.sku || "").toLowerCase().includes(s)).slice(0, 8);
  }, [products, esearch]);

  const addEitem = (p) => {
    setEcart((prev) => {
      const ex = prev.find((c) => c.product.id === p.id);
      if (ex) return prev.map((c) => c.product.id === p.id ? { ...c, qty: Math.min(num(c.qty) + 1, maxQty(p)) } : c);
      return [...prev, { product: p, qty: 1, discount: 0 }];
    });
    setEsearch("");
  };
  const setEqty = (id, v) => setEcart((p) => p.map((c) => c.product.id === id ? { ...c, qty: v } : c));
  const setEdisc = (id, v) => setEcart((p) => p.map((c) => c.product.id === id ? { ...c, discount: v } : c));
  const rmEitem = (id) => setEcart((p) => p.filter((c) => c.product.id !== id));

  const doEdit = useMutation({
    mutationFn: () => {
      const items = ecart
        .filter((c) => num(c.qty) > 0)
        .map((c) => ({ product_id: c.product.id, quantity: num(c.qty), discount: num(c.discount) }));
      if (!items.length) return Promise.reject({ response: { data: { detail: "Add at least one item" } } });
      if ((emethod === "credit" || emethod === "partial") && !eclientId)
        return Promise.reject({ response: { data: { detail: "Select a client for credit / partial sales" } } });
      return api.put(`/sales/${editSale.id}`, {
        items,
        discount: parseFloat(ediscount) || 0,
        payment_method: emethod,
        paid_amount: emethod === "partial" ? parseFloat(epaid) || 0 : 0,
        client_id: (emethod === "credit" || emethod === "partial") ? eclientId : null,
        date: edate && edate !== eOrigDate ? edate : undefined,
      });
    },
    onSuccess: () => { toast.success("Bill updated"); invalidateSaleQueries(qc); setEditSale(null); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const openReturn = (s) => {
    setRetSale(s);
    setRetItems(Object.fromEntries(s.items.map((it) => [it.product_id, 0])));
    setRetSettle(s.client_id ? "credit" : "cash");
  };
  const doReturn = useMutation({
    mutationFn: () => {
      const items = Object.entries(retItems)
        .filter(([, q]) => (parseFloat(q) || 0) > 0)
        .map(([product_id, q]) => ({ product_id, quantity: parseFloat(q) }));
      if (!items.length) return Promise.reject({ response: { data: { detail: "Enter a quantity to return" } } });
      return api.post(`/sales/${retSale.id}/return`, { items, settlement: retSettle });
    },
    onSuccess: () => { toast.success("Return recorded"); invalidateSaleQueries(qc); setRetSale(null); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const dt = (v) => new Date(v).toLocaleDateString(undefined, { timeZone: "Asia/Karachi" });

  return (
    <div className="space-y-6" data-testid="sales-history-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="label-cap">History</div><h1 className="font-heading text-3xl mt-1">Sales History</h1></div>
      </div>

      {/* Summary (managers) */}
      {canEdit && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Total Sales" value={fmtMoney(repAll?.total_revenue || 0)} icon={ShoppingBag} hint={`${repAll?.count ?? 0} transactions`} />
          <Stat label="This Month" value={fmtMoney(repMonth?.total_revenue || 0)} icon={CalendarDays} tone="amber" hint="Sales this month" />
          <Stat label="Cash from Sales" value={fmtMoney(fp?.breakdown?.cash_from_sales || 0)} icon={Wallet} hint="Received in cash" />
          <Stat label="Receivable" value={fmtMoney(fp?.receivable || 0)} icon={HandCoins} tone={(fp?.receivable || 0) > 0 ? "danger" : "forest"} hint="Owed by customers" />
        </div>
      )}

      {/* Filters */}
      <div className="card-soft p-4 flex flex-wrap items-end gap-3">
        <div><label className="block text-xs text-agri-ink-2 mb-1">From</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls + " w-40"} /></div>
        <div><label className="block text-xs text-agri-ink-2 mb-1">To</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls + " w-40"} /></div>
        <div><label className="block text-xs text-agri-ink-2 mb-1">Method</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls + " w-40"}>
            <option value="all">All</option><option value="cash">Cash</option><option value="credit">Credit</option><option value="partial">Partial</option>
          </select></div>
        {(start || end || method !== "all") && (
          <button onClick={() => { setStart(""); setEnd(""); setMethod("all"); }} className="text-xs text-agri-ink-2 hover:text-agri-forest underline pb-2">Clear</button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgba(28,28,30,0.1)]">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === t.key ? "border-agri-forest text-agri-forest" : "border-transparent text-agri-ink-2 hover:text-agri-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="text-xs text-agri-ink-2">{total} {tab === "returns" ? "return" : "sale"}{total === 1 ? "" : "s"}</div>
      <TableCard>
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>Invoice</th><th className={thCls}>Date</th><th className={thCls}>Client</th>
            <th className={thCls}>Salesperson</th><th className={thCls}>Method</th><th className={thCls}>Status</th>
            <th className={thNumCls}>Total</th><th className={thCls}></th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <SkeletonRows cols={8} />}
          {!isLoading && rows.length === 0 && <EmptyRow colSpan={8} icon={tab === "returns" ? Undo2 : Receipt}>{tab === "returns" ? "No returns in range." : "No sales in range."}</EmptyRow>}
          {!isLoading && rows.map((s) => (
            <Fragment key={s.id}>
              <tr onClick={() => setExpanded(expanded === s.id ? null : s.id)} className={`${rowCls} cursor-pointer`}>
                <td className={`${tdCls} font-mono text-xs rt-primary`}>
                  {s.invoice_no}
                  {s.is_return && <span className="ml-1.5 text-[10px] font-sans font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-agri-danger align-middle">RETURN</span>}
                </td>
                <td data-label="Date" className={`${tdCls} text-agri-ink-2 whitespace-nowrap`}>{dt(s.created_at)}</td>
                <td data-label="Client" className={tdCls}>{s.client_name || <span className="text-agri-ink-2">Walk-in</span>}</td>
                <td data-label="Salesperson" className={`${tdCls} text-agri-ink-2`}>{s.salesperson_name}</td>
                <td data-label="Method" className={`${tdCls} capitalize`}>{s.payment_method}</td>
                <td data-label="Status" className={tdCls}><StatusBadge status={s.status} /></td>
                <td data-label="Total" className={`${tdNumCls} font-semibold ${s.is_return ? "text-agri-danger" : ""}`}>{fmtMoney(s.total)}</td>
                <td className={`${tdCls} text-right whitespace-nowrap rt-actions`}>
                  <button data-testid={`download-invoice-${s.id}`} onClick={(e) => { e.stopPropagation(); downloadInvoice(s); }} title="Download invoice PDF"
                    className="p-1.5 rounded hover:bg-agri-amber/10 text-agri-ink-2 hover:text-agri-amber-dark align-middle">
                    <Download size={15} />
                  </button>
                  {canEdit && !s.is_return && (
                    <button data-testid={`edit-sale-${s.id}`} onClick={(e) => { e.stopPropagation(); openEdit(s); }} title="Edit this bill"
                      className="p-1.5 rounded hover:bg-agri-forest/10 text-agri-ink-2 hover:text-agri-forest align-middle">
                      <Pencil size={15} />
                    </button>
                  )}
                  {canReturn && !s.is_return && (
                    <button data-testid={`return-sale-${s.id}`} onClick={(e) => { e.stopPropagation(); openReturn(s); }} title="Record a return"
                      className="p-1.5 rounded hover:bg-red-50 text-agri-ink-2 hover:text-agri-danger align-middle">
                      <Undo2 size={15} />
                    </button>
                  )}
                  <ChevronDown size={15} className={`inline-block ml-1 text-agri-ink-2 transition-transform ${expanded === s.id ? "rotate-180" : ""}`} />
                </td>
              </tr>
              {expanded === s.id && (
                <tr><td colSpan={8} className="bg-agri-bg/40 p-4 rt-full">
                  <div className="text-xs uppercase tracking-wider text-agri-ink-2 mb-2">Items</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {s.items.map((it, i) => (
                      <div key={i} className="bg-white rounded-lg p-2.5 text-sm flex justify-between border border-[rgba(28,28,30,0.04)]">
                        <span>{it.product_name} <span className="text-agri-ink-2">× {Math.abs(it.quantity)}</span></span>
                        <span className="font-semibold tabular-nums">{fmtMoney(it.line_total)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs text-agri-ink-2 tabular-nums">Paid: {fmtMoney(s.paid_amount)} · Credit: {fmtMoney(s.credit_amount)} · Profit: {fmtMoney(s.profit)}</div>
                </td></tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </TableCard>
      <Pagination page={page} pageSize={PAGE} total={total} onPage={setPage} />

      <Modal open={!!retSale} onClose={() => setRetSale(null)} title="Record Return" size="lg">
        {retSale && (
          <form onSubmit={(e) => { e.preventDefault(); doReturn.mutate(); }} className="space-y-4">
            <div className="text-sm text-agri-ink-2">
              Invoice <span className="font-mono">{retSale.invoice_no}</span> · {retSale.client_name || "Walk-in"}
            </div>
            <div className="space-y-2">
              <div className="label-cap">Items to return</div>
              {retSale.items.map((it) => (
                <div key={it.product_id} className="flex items-center justify-between gap-3 bg-agri-bg/40 rounded-lg p-2.5">
                  <div className="text-sm">{it.product_name} <span className="text-agri-ink-2">· sold {it.quantity}</span></div>
                  <div className="w-28">
                    <NumberInput value={retItems[it.product_id] ?? 0} max={it.quantity}
                      onChange={(n) => setRetItems((m) => ({ ...m, [it.product_id]: Math.min(Number(n) || 0, it.quantity) }))} />
                  </div>
                </div>
              ))}
            </div>
            <div>
              <label className={labelCls}>How to settle the money</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => retSale.client_id && setRetSettle("credit")} disabled={!retSale.client_id}
                  className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${retSettle === "credit" ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"} ${!retSale.client_id ? "opacity-40 cursor-not-allowed" : ""}`}>
                  Reduce their credit
                </button>
                <button type="button" onClick={() => setRetSettle("cash")}
                  className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${retSettle === "cash" ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"}`}>
                  Cash refund
                </button>
              </div>
              <p className="text-[11px] text-agri-ink-2 mt-1">
                {retSettle === "credit" ? "Lowers what this client owes you." : "Records money paid back to the customer."}
                {!retSale.client_id && " (Walk-in sale — only cash refund is possible.)"}
              </p>
            </div>
            <p className="text-[11px] text-agri-ink-2">Stock goes back up and the sale's revenue/profit reverse — reflected on the dashboard, reports and cashbook.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setRetSale(null)} className={btnOutline}>Cancel</button>
              <button className={btnPrimary} disabled={doReturn.isPending}>{doReturn.isPending ? "Saving…" : "Record return"}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!editSale} onClose={() => setEditSale(null)} title={`Edit Bill · ${editSale?.invoice_no || ""}`} size="lg">
        {editSale && (
          <form onSubmit={(e) => { e.preventDefault(); doEdit.mutate(); }} className="space-y-4">
            <div>
              <label className={labelCls}>Add / find a product</label>
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-agri-ink-2" />
                <input value={esearch} onChange={(e) => setEsearch(e.target.value)} placeholder="Search by name or SKU…" className={inputCls + " pl-9"} />
                {eFiltered.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-[rgba(28,28,30,0.12)] rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {eFiltered.map((p) => (
                      <button key={p.id} type="button" onClick={() => addEitem(p)}
                        className="w-full text-left px-3 py-2 hover:bg-agri-bg flex items-center justify-between gap-2">
                        <span className="text-sm truncate">{p.name}</span>
                        <span className="text-xs text-agri-ink-2 shrink-0">{fmtMoney(p.selling_price)} · {fmt(p.stock_qty)} {p.unit}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 max-h-[38vh] overflow-y-auto -mr-1 pr-1">
              {ecart.length === 0 && <div className="text-sm text-agri-ink-2 py-4 text-center">No items — add at least one.</div>}
              {ecart.map((c) => (
                <div key={c.product.id} className="border border-[rgba(28,28,30,0.06)] rounded-lg p-2.5">
                  <div className="flex justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.product.name}</div>
                      <div className="text-xs text-agri-ink-2">
                        {fmtMoney(c.product.selling_price)} / {c.product.unit || "unit"}
                        {c.product.stock_qty !== Infinity && <> · max {fmt(maxQty(c.product))}</>}
                      </div>
                    </div>
                    <button type="button" onClick={() => rmEitem(c.product.id)} className="text-agri-ink-2 hover:text-agri-danger"><Trash2 size={14} /></button>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 items-center">
                    <input type="number" step="1" min="0" value={c.qty} onFocus={(e) => e.target.select()}
                      onChange={(e) => setEqty(c.product.id, e.target.value)} className={inputCls + " text-xs py-1"} placeholder="Qty" />
                    <input type="number" step="1" min="0" value={c.discount || ""}
                      onChange={(e) => setEdisc(c.product.id, e.target.value)} className={inputCls + " text-xs py-1"} placeholder="Disc" />
                    <div className="text-right text-sm font-semibold tabular-nums">{fmtMoney(Math.max(0, num(c.qty) * c.product.selling_price - num(c.discount)))}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[rgba(28,28,30,0.06)] pt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-agri-ink-2">Subtotal</span><span className="tabular-nums">{fmtMoney(eSubtotal)}</span></div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-agri-ink-2">Bill discount</span>
                <NumberInput value={ediscount} onChange={setEdiscount} className="w-28 py-1 text-right" />
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-[rgba(28,28,30,0.06)]">
                <span>Total</span><span className="text-agri-forest tabular-nums">{fmtMoney(eTotal)}</span>
              </div>
            </div>

            <div>
              <label className={labelCls}>Bill date</label>
              <input type="date" value={edate} onChange={(e) => setEdate(e.target.value || eOrigDate)} className={inputCls} />
              {edate !== eOrigDate && <div className="text-xs text-agri-amber-dark mt-1">Changed to {dt(edate)}</div>}
            </div>
            <div>
              <label className={labelCls}>Payment method</label>
              <div className="grid grid-cols-3 gap-2">
                {["cash", "credit", "partial"].map((m) => (
                  <button key={m} type="button" onClick={() => setEmethod(m)}
                    className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors capitalize ${emethod === m ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"}`}>{m}</button>
                ))}
              </div>
            </div>
            {(emethod === "credit" || emethod === "partial") && (
              <div>
                <label className={labelCls}>Client</label>
                <select value={eclientId} onChange={(e) => setEclientId(e.target.value)} className={inputCls}>
                  <option value="">Select client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.area ? ` — ${c.area}` : ""}</option>)}
                </select>
              </div>
            )}
            {emethod === "partial" && (
              <div>
                <label className={labelCls}>Amount received</label>
                <NumberInput value={epaid} max={eTotal} onChange={setEpaid} />
                <div className="text-xs text-agri-ink-2 mt-1">Credit: {fmtMoney(Math.max(0, eTotal - (parseFloat(epaid) || 0)))}</div>
              </div>
            )}

            <p className="text-[11px] text-agri-ink-2">
              Saving reverses the original bill and re-applies these changes — stock, the client's credit, profit, cash and reports all update. Item prices use each product's current selling price.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setEditSale(null)} className={btnOutline}>Cancel</button>
              <button className={btnPrimary} disabled={doEdit.isPending}>{doEdit.isPending ? "Saving…" : "Save changes"}</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
